import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import Modal from '../modal/modal.view';
import { api } from '../../api/endpoints';
import AppStore from '../../app.store';
import {
  loadChatsFromStorage,
  saveChatsToStorage,
  generateChatId,
  type ChatSession,
  type ChatMessage,
} from './chat.types';
import styled, { keyframes } from 'styled-components';
import {media, sizes, borderColor, cardBackground, mutedForeground, primaryColor, primaryColorTransparent, primaryForeground, primaryHover, sidebarBackground, textColorDark} from '../../utils/variables';
import { formatRecordingMmSs, SPEECH_TO_TEXT_MAX_MS, useSpeechToText } from '../../hooks/useSpeechToText';
import { useIsCompactLayout } from '../../hooks/useMediaQuery';

function MicIcon(props: { className?: string }) {
  return (
    <svg
      className={props.className}
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.49 6-3.31 6-6.72h-1.7z"
      />
    </svg>
  );
}

const FullscreenInner = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  min-width: 0;
  box-sizing: border-box;

  ${media.mobile} {
    min-width: 0;
    width: 100vw;
    padding: 0;
    margin: 0 -15px;
    width: calc(100% + 30px);
  }

  ${media.tablet} {
    min-width: 0;
    width: 100vw;
    padding: 0;
    margin: 0 -15px;
    width: calc(100% + 30px);
  }
`;

const ChatLayout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  position: relative;

  ${media.mobile} {
    flex-direction: column;
  }

  ${media.tablet} {
    flex-direction: column;
  }
`;

const Sidebar = styled.div`
  width: 240px;
  border-right: 1px solid ${borderColor};
  display: flex;
  flex-direction: column;
  background: ${sidebarBackground};

  ${media.mobile} {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${borderColor};
    max-height: 35vh;
  }

  ${media.tablet} {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid ${borderColor};
    max-height: 35vh;
  }
`;

const MobileSidebarOverlay = styled.button<{ open: boolean }>`
  display: none;

  ${media.mobile} {
    display: ${(p) => (p.open ? 'block' : 'none')};
    position: absolute;
    inset: 0;
    border: none;
    background: rgba(0, 0, 0, 0.45);
    z-index: 5;
    padding: 0;
    margin: 0;
    cursor: pointer;
  }

  ${media.tablet} {
    display: ${(p) => (p.open ? 'block' : 'none')};
    position: absolute;
    inset: 0;
    border: none;
    background: rgba(0, 0, 0, 0.45);
    z-index: 5;
    padding: 0;
    margin: 0;
    cursor: pointer;
  }
`;

const FloatingSidebar = styled(Sidebar)<{ open: boolean }>`
  ${media.mobile} {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: min(85vw, 320px);
    max-height: none;
    border-right: none;
    border-bottom: none;
    background: var(--card);
    z-index: 6;
    transform: translateX(${(p) => (p.open ? '0' : '-105%')});
    transition: transform 0.2s ease;
  }

  ${media.tablet} {
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: min(70vw, 360px);
    max-height: none;
    border-right: none;
    border-bottom: none;
    background: var(--card);
    z-index: 6;
    transform: translateX(${(p) => (p.open ? '0' : '-105%')});
    transition: transform 0.2s ease;
  }
`;

const MessagesHeader = styled.div`
  display: none;

  ${media.mobile} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border-bottom: 1px solid ${borderColor};
    background: var(--card);
  }

  ${media.tablet} {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 10px 12px;
    border-bottom: 1px solid ${borderColor};
    background: var(--card);
  }
`;

const MobileChatsBtn = styled.button`
  border: 1px solid ${borderColor};
  border-radius: 10px;
  background: var(--card);
  color: ${textColorDark};
  font-size: 13px;
  font-weight: 600;
  padding: 8px 12px;
  cursor: pointer;
`;

const SidebarTitle = styled.div`
  padding: 12px 16px;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid ${borderColor};
`;

const ChatList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
`;

const DeleteBtn = styled.span`
  color: #ff4d4f;
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  opacity: 0;
  transition: opacity 0.2s ease;

  &:hover {
    background: #ffccc7;
  }

  ${media.mobile} {
    opacity: 1;
  }

  ${media.tablet} {
    opacity: 1;
  }
`;

const ChatItem = styled.div<{ active?: boolean }>`
  padding: 10px 16px;
  cursor: pointer;
  font-size: 13px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background: ${(p) => (p.active ? primaryColorTransparent : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  &:hover {
    background: ${(p) => (p.active ? primaryColorTransparent : 'var(--nav-item-hover-bg)')};
  }
  &:hover ${DeleteBtn} {
    opacity: 1;
  }
`;

const ChatItemTitle = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChatItemTitleInput = styled.input`
  flex: 1;
  min-width: 0;
  font-size: 13px;
  border: 1px solid ${primaryColor};
  border-radius: 4px;
  padding: 4px 8px;
  outline: none;
  background: var(--card);
`;

const NewChatBtn = styled.button`
  margin: 8px 12px;
  padding: 8px 12px;
  border: 1px dashed ${borderColor};
  background: var(--card);
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: ${mutedForeground};

  &:hover {
    border-color: ${primaryColor};
    color: ${primaryColor};
  }
`;

const MessagesArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 0;

  ${media.mobile} {
    width: 100%;
  }

  ${media.tablet} {
    width: 100%;
  }
`;

const MessagesList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MessageBubble = styled.div<{ role: 'user' | 'model' }>`
  max-width: 85%;
  align-self: ${(p) => (p.role === 'user' ? 'flex-end' : 'flex-start')};
  padding: 12px 16px;
  border-radius: 12px;
  background: ${(p) => (p.role === 'user' ? primaryColor : 'var(--muted)')};
  color: ${(p) => (p.role === 'user' ? primaryForeground : 'var(--foreground)')};
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;
  ${(p) => (p.role === 'user' ? 'white-space: pre-wrap;' : '')}
`;

const MarkdownContent = styled.div`
  & p { margin: 0 0 0.75em; }
  & p:last-child { margin-bottom: 0; }
  & strong { font-weight: 600; color: inherit; }
  & em { font-style: italic; }
  & ul { margin: 0.5em 0; padding-left: 1.5em; }
  /* Нумерацию отрисовываем вручную из текста markdown */
  & ol { margin: 0.5em 0; padding-left: 0; list-style: none; }
  & li { margin: 0.25em 0; }
  & h2, & h3 { margin: 1em 0 0.5em; font-weight: 600; line-height: 1.3; }
  & h2 { font-size: 1.15em; }
  & h3 { font-size: 1.05em; }
  & code {
    background: rgba(0,0,0,0.06);
    padding: 0.2em 0.4em;
    border-radius: 4px;
    font-size: 0.9em;
    font-family: ui-monospace, monospace;
  }
  & a { color: ${primaryColor}; text-decoration: none; }
  & a:hover { text-decoration: underline; }
`;

/** Лёгкий рендер Markdown без внешних пакетов: **жирный**, *курсив*, `код`, списки, заголовки */
function renderSimpleMarkdown(text: string): React.ReactNode {
  const lines = text.split(/\n/);
  const out: React.ReactNode[] = [];
  let listItems: Array<string | { num: number; text: string }> = [];
  let listOrdered: boolean | null = null;
  let key = 0;

  const flushList = () => {
    if (listItems.length === 0) return;
    const Tag = listOrdered ? 'ol' : 'ul';
    out.push(
      React.createElement(
        Tag,
        { key: key++ },
        ...listItems.map((item) =>
          React.createElement(
            'li',
            { key: key++ },
            typeof item === 'string' ? (
              parseInline(item)
            ) : (
              <>
                <span style={{ fontWeight: 700, marginRight: 8 }}>{item.num}.</span>
                {parseInline(item.text)}
              </>
            )
          )
        )
      )
    );
    listItems = [];
    listOrdered = null;
  };

  const parseInline = (s: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let rest = s;
    let k = 0;
    const re = /(\*\*(.+?)\*\*|__(.+?)__|\*(.+?)\*|_(.+?)_|`([^`]+)`|\[([^\]]+)\]\(([^)]+)\))/g;
    let lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(rest)) !== null) {
      if (m.index > lastIndex) {
        parts.push(rest.slice(lastIndex, m.index));
      }
      if (m[2] !== undefined) parts.push(React.createElement('strong', { key: k++ }, m[2]));
      else if (m[3] !== undefined) parts.push(React.createElement('strong', { key: k++ }, m[3]));
      else if (m[4] !== undefined) parts.push(React.createElement('em', { key: k++ }, m[4]));
      else if (m[5] !== undefined) parts.push(React.createElement('em', { key: k++ }, m[5]));
      else if (m[6] !== undefined) parts.push(React.createElement('code', { key: k++ }, m[6]));
      else if (m[7] !== undefined && m[8] !== undefined)
        parts.push(React.createElement('a', { key: k++, href: m[8], target: '_blank', rel: 'noopener noreferrer' }, m[7]));
      lastIndex = re.lastIndex;
    }
    if (lastIndex < rest.length) parts.push(rest.slice(lastIndex));
    return parts.length === 1 ? parts[0] : React.createElement(React.Fragment, {}, ...parts);
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const bullet = line.match(/^(\s*)[-*]\s+(.*)$/);
    const numbered = line.match(/^(\s*)(\d+)\.\s+(.*)$/);
    const h2 = line.match(/^##\s+(.*)$/);
    const h3 = line.match(/^###\s+(.*)$/);

    if (bullet) {
      if (listOrdered !== false) flushList();
      listOrdered = false;
      listItems.push(bullet[2]);
      continue;
    }
    if (numbered) {
      if (listOrdered !== true) flushList();
      listOrdered = true;
      listItems.push({ num: Number(numbered[2]), text: numbered[3] });
      continue;
    }
    // Пустые строки внутри списка не должны "разрывать" список.
    // Иначе каждый следующий элемент попадает в новый `<ol>`, который снова нумеруется с 1.
    if (line.trim() === '') continue;

    flushList();

    if (h2) {
      out.push(React.createElement('h2', { key: key++ }, parseInline(h2[1])));
      continue;
    }
    if (h3) {
      out.push(React.createElement('h3', { key: key++ }, parseInline(h3[1])));
      continue;
    }
    out.push(React.createElement('p', { key: key++ }, parseInline(line)));
  }
  flushList();
  return React.createElement(React.Fragment, {}, ...out);
}

const InputRow = styled.div<{ $recording?: boolean }>`
  flex-shrink: 0;
  padding: 16px;
  border-top: 1px solid ${borderColor};
  background: var(--card);
  display: flex;
  gap: 12px;
  align-items: ${(p) => (p.$recording ? 'stretch' : 'flex-end')};
  flex-wrap: ${(p) => (p.$recording ? 'wrap' : 'nowrap')};

  ${media.mobile} {
    position: sticky;
    bottom: 0;
    z-index: 4;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
    gap: 10px;
    align-items: stretch;
    flex-direction: column;
  }

  /* До desktop: как на телефоне — колонка, иначе микрофон и «Отправить» сжимаются в одну строку */
  ${media.tablet} {
    position: sticky;
    bottom: 0;
    z-index: 4;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
    gap: 10px;
    align-items: stretch;
    flex-direction: column;
  }
`;

const INPUT_MIN_HEIGHT = 44;
const INPUT_MAX_HEIGHT = 120;

const Input = styled.textarea`
  flex: 1;
  min-height: ${INPUT_MIN_HEIGHT}px;
  max-height: ${INPUT_MAX_HEIGHT}px;
  padding: 10px 12px;
  border: 1px solid ${borderColor};
  border-radius: 8px;
  font-size: 14px;
  resize: none;
  font-family: inherit;
  overflow-y: auto;
  background: ${cardBackground};
  color: var(--foreground);

  &:focus {
    outline: none;
    border-color: ${primaryColor};
  }
`;

const SendBtn = styled.button`
  padding: 10px 20px;
  background: ${primaryColor};
  color: ${primaryForeground};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  height: 44px;
  min-width: 110px;

  &:hover:not(:disabled) {
    background: ${primaryHover};
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  ${media.mobile},
  ${media.tablet} {
    min-width: 0;
    flex: 1;
    height: 42px;
    padding: 10px 14px;
    font-size: 13px;
  }
`;

const pulseDot = keyframes`
  0%, 100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.45);
  }
  50% {
    transform: scale(1.06);
    box-shadow: 0 0 0 8px rgba(255, 77, 79, 0);
  }
`;

const waveBar = keyframes`
  0%, 100% {
    transform: scaleY(0.35);
    opacity: 0.65;
  }
  50% {
    transform: scaleY(1);
    opacity: 1;
  }
`;

const spin = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

const InputWrap = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const VoiceProcessingStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: ${primaryColorTransparent};
  border: 1px solid ${primaryColor};
  border-radius: 8px;
  font-size: 13px;
  color: ${primaryColor};
`;

const SpinnerIcon = styled.span`
  width: 18px;
  height: 18px;
  border: 2px solid ${primaryColorTransparent};
  border-top-color: ${primaryColor};
  border-radius: 50%;
  flex-shrink: 0;
  animation: ${spin} 0.7s linear infinite;
`;

const VoiceRecordingCard = styled.div`
  flex: 1 1 100%;
  width: 100%;
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 10px 14px;
  background: linear-gradient(135deg, var(--danger-subtle) 0%, var(--card) 55%);
  border: 1px solid #ffccc7;
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(255, 77, 79, 0.08);

  ${media.mobile} {
    flex-wrap: wrap;
    gap: 12px;
  }
`;

const VoicePulseWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const VoicePulseDot = styled.span`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ff4d4f;
  animation: ${pulseDot} 1.25s ease-in-out infinite;
`;

const VoiceRecordingMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const VoiceRecordingTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const VoiceRecordingTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${textColorDark};
`;

const VoiceRecordingHint = styled.span`
  font-size: 12px;
  color: ${mutedForeground};
  line-height: 1.35;
`;

const VoiceTimer = styled.span`
  font-variant-numeric: tabular-nums;
  font-size: 17px;
  font-weight: 700;
  color: #cf1322;
  letter-spacing: 0.03em;
  flex-shrink: 0;
`;

const VoiceWaveform = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  height: 28px;
  flex: 1;
  justify-content: center;
  min-width: 48px;

  ${media.mobile} {
    order: 3;
    width: 100%;
    flex: none;
    justify-content: center;
    padding: 4px 0;
  }
`;

const VoiceWaveBar = styled.span<{ $delay: number }>`
  width: 4px;
  height: 100%;
  max-height: 28px;
  border-radius: 2px;
  background: #ff7875;
  transform-origin: center bottom;
  animation: ${waveBar} 0.9s ease-in-out infinite;
  animation-delay: ${(p) => p.$delay}ms;
`;

const VoiceRecordingActions = styled.div`
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;

  ${media.mobile} {
    margin-left: 0;
    width: 100%;
    justify-content: stretch;

    & > button {
      flex: 1;
    }
  }
`;

const VoiceBtnGhost = styled.button`
  height: 36px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid ${borderColor};
  background: var(--card);
  color: ${textColorDark};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition:
    border-color 0.15s,
    color 0.15s;

  &:hover {
    border-color: ${primaryColor};
    color: ${primaryColor};
  }

  &:focus-visible {
    outline: 2px solid ${primaryColor};
    outline-offset: 2px;
  }
`;

const VoiceBtnPrimary = styled.button`
  height: 36px;
  padding: 0 16px;
  border-radius: 8px;
  border: none;
  background: #ff4d4f;
  color: var(--primary-foreground);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #ff7875;
  }

  &:focus-visible {
    outline: 2px solid #ff4d4f;
    outline-offset: 2px;
  }
`;

const InputActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  flex-shrink: 0;

  ${media.mobile},
  ${media.tablet} {
    width: 100%;
  }
`;

const MicBtn = styled.button<{ $busy?: boolean }>`
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${borderColor};
  border-radius: 8px;
  cursor: pointer;
  background: ${(p) => (p.$busy ? 'var(--muted)' : 'var(--card)')};
  color: ${(p) => (p.$busy ? mutedForeground : textColorDark)};
  transition:
    border-color 0.15s,
    background 0.15s,
    color 0.15s;

  &:hover:not(:disabled) {
    border-color: ${primaryColor};
    color: ${primaryColor};
    background: var(--table-row-hover);
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${primaryColor};
    outline-offset: 2px;
  }

  ${media.mobile},
  ${media.tablet} {
    flex: 0 0 44px;
    height: 42px;
  }
`;

const VoiceHint = styled.div`
  font-size: 12px;
  color: #cf1322;
  line-height: 1.4;
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${mutedForeground};
  font-size: 14px;
  text-align: center;
  padding: 0 16px;
`;

export const ChatModalView = observer(
  (props: { onClose: () => void }) => {
    const { onClose } = props;
    const [chats, setChats] = useState<ChatSession[]>(() =>
      loadChatsFromStorage().map((c) => ({
        ...c,
        messages: c.messages.slice(-200),
      }))
    );
    const [currentId, setCurrentId] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [streamingText, setStreamingText] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingChatId, setEditingChatId] = useState<string | null>(null);
    const editInitialTitleRef = useRef<string>('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const abortRef = useRef<AbortController | null>(null);
    const isMobileLayout = useIsCompactLayout();
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);

    useEffect(() => {
      setIsSidebarOpen(!isMobileLayout);
    }, [isMobileLayout]);

    const appendTranscribed = useCallback((text: string) => {
      setInputValue((prev) => (prev ? `${prev} ${text}` : text));
    }, []);

    const {
      voiceSupported,
      voiceErr,
      setVoiceErr,
      speechLoading,
      isRecording,
      recordElapsedSec,
      startRecording,
      cancelVoiceRecording,
      finishVoiceRecording,
    } = useSpeechToText({
      isExternallyBusy: loading,
      speechContext: 'chat',
      onTranscribedText: appendTranscribed,
    });

    const adjustInputHeight = useCallback(() => {
      const el = inputRef.current;
      if (!el) return;
      el.style.height = 'auto';
      const h = Math.min(INPUT_MAX_HEIGHT, Math.max(INPUT_MIN_HEIGHT, el.scrollHeight));
      el.style.height = h + 'px';
    }, []);

    const currentChat = chats.find((c) => c.id === currentId) ?? null;

    useEffect(() => {
      saveChatsToStorage(chats);
    }, [chats]);

    useEffect(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [currentChat?.messages, streamingText]);

    useEffect(() => {
      adjustInputHeight();
    }, [inputValue, adjustInputHeight]);

    const createNewChat = useCallback(() => {
      const id = generateChatId();
      const newChat: ChatSession = {
        id,
        title: 'Новый чат',
        messages: [],
        createdAt: Date.now(),
      };
      setChats((prev) => [newChat, ...prev]);
      setCurrentId(id);
      setStreamingText('');
      if (isMobileLayout) setIsSidebarOpen(false);
    }, [isMobileLayout]);

    const deleteChat = useCallback((id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      setChats((prev) => prev.filter((c) => c.id !== id));
      if (currentId === id) {
        setCurrentId(null);
        setStreamingText('');
      }
      if (editingChatId === id) setEditingChatId(null);
    }, [currentId, editingChatId]);

    const startEditTitle = useCallback((id: string, title: string) => {
      setEditingChatId(id);
      editInitialTitleRef.current = title;
    }, []);

    const saveEditTitle = useCallback(
      (id: string) => {
        setChats((prev) =>
          prev.map((c) =>
            c.id === id
              ? { ...c, title: (c.title || '').trim() || 'Без названия' }
              : c
          )
        );
        setEditingChatId(null);
      },
      []
    );

    const cancelEditTitle = useCallback(() => {
      setChats((prev) =>
        prev.map((c) =>
          c.id === editingChatId ? { ...c, title: editInitialTitleRef.current } : c
        )
      );
      setEditingChatId(null);
    }, [editingChatId]);

    const sendMessage = useCallback(async () => {
      const text = inputValue.trim();
      if (!text || loading) return;

      const token = AppStore.getToken?.();
      if (!token) return;

      let chatId = currentId;
      const userMessage: ChatMessage = { role: 'user', text };

      if (!chatId) {
        chatId = generateChatId();
        const newChat: ChatSession = {
          id: chatId,
          title: (text.slice(0, 50).trim() || 'Новый чат') + (text.length > 50 ? '…' : ''),
          messages: [userMessage],
          createdAt: Date.now(),
        };
        setChats((prev) => [newChat, ...prev]);
        setCurrentId(chatId);
      } else {
        setChats((prev) =>
          prev.map((c) => {
            if (c.id !== chatId) return c;
            const isFirstMessage = c.messages.length === 0;
            const title = isFirstMessage
              ? (text.slice(0, 50).trim() || 'Новый чат') + (text.length > 50 ? '…' : '')
              : c.title;
            return { ...c, title, messages: [...c.messages, userMessage] };
          })
        );
      }

      const chat = chats.find((c) => c.id === chatId);
      const recentMessages = chat ? chat.messages.slice(-200) : [];
      const history: { role: 'user' | 'model'; text: string }[] = recentMessages.map((m) => ({
        role: m.role,
        text: m.text,
      }));

      setInputValue('');
      setLoading(true);
      setStreamingText('');

      abortRef.current = new AbortController();
      let fullResponse = '';

      const doRequest = async (accessToken: string): Promise<Response> => {
        return fetch(api.chat_stream, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + accessToken,
          },
          body: JSON.stringify({
            message: text,
            history: history.map((h) => ({ role: h.role, text: h.text })),
          }),
          signal: abortRef.current?.signal,
        });
      };

      try {
        let res = await doRequest(token);

        if (res.status === 401) {
          const newToken = await AppStore.refreshAccessToken();
          if (newToken) {
            abortRef.current = new AbortController();
            res = await doRequest(newToken);
          } else {
            setStreamingText('Сессия истекла. Войдите снова.');
            return;
          }
        }

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          setStreamingText('Ошибка: ' + (err?.error || res.statusText));
          return;
        }

        const reader = res.body?.getReader();
        if (!reader) {
          setStreamingText('Ошибка: нет потока ответа');
          return;
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6).trim();
              if (jsonStr === '[DONE]' || jsonStr === '') continue;
              try {
                const data = JSON.parse(jsonStr) as { text?: string; error?: string };
                if (data.error) {
                  // Не добавляем ошибки в ответ и не сохраняем их в чат
                } else if (data.text) {
                  fullResponse += data.text;
                  setStreamingText(fullResponse);
                }
              } catch {
                // skip invalid json
              }
            }
          }
        }
      } catch (e) {
        if ((e as Error).name === 'AbortError') return;
        setStreamingText('Ошибка запроса: ' + (e as Error).message);
        return;
      } finally {
        setLoading(false);
        abortRef.current = null;
      }

      if (fullResponse.trim()) {
        const modelMessage: ChatMessage = { role: 'model', text: fullResponse };
        setChats((prev) =>
          prev.map((c) => {
            if (c.id !== chatId) return c;
            const nextMessages = [...c.messages, modelMessage].slice(-200);
            return { ...c, messages: nextMessages };
          })
        );
      }
      setStreamingText('');
    }, [inputValue, loading, currentId, chats]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        sendMessage();
      }
    };

    const allMessages = currentChat?.messages ?? [];
    const messages = allMessages.slice(-200);
    const showEmpty = !currentId || (messages.length === 0 && !streamingText && !loading);

    return (
      <Modal title="Помощник Visor" onClose={onClose} fullScreen>
        <FullscreenInner>
          <ChatLayout>
            <MobileSidebarOverlay
              type="button"
              open={isMobileLayout && isSidebarOpen}
              onClick={() => setIsSidebarOpen(false)}
              aria-label="Скрыть список чатов"
            />
            <FloatingSidebar open={!isMobileLayout || isSidebarOpen}>
              <SidebarTitle>Чаты</SidebarTitle>
              <NewChatBtn type="button" onClick={createNewChat}>
                + Новый чат
              </NewChatBtn>
              <ChatList>
                {chats.map((c) => (
                  <ChatItem
                    key={c.id}
                    active={c.id === currentId}
                    onClick={() => {
                      if (editingChatId !== c.id) {
                        setCurrentId(c.id);
                        setStreamingText('');
                        if (isMobileLayout) setIsSidebarOpen(false);
                      }
                    }}
                  >
                    {editingChatId === c.id ? (
                      <ChatItemTitleInput
                        value={c.title}
                        onChange={(e) =>
                          setChats((prev) =>
                            prev.map((ch) =>
                              ch.id === c.id ? { ...ch, title: e.target.value } : ch
                            )
                          )
                        }
                        onBlur={() => saveEditTitle(c.id)}
                        onKeyDown={(e) => {
                          e.stopPropagation();
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                          if (e.key === 'Escape') cancelEditTitle();
                        }}
                        onClick={(e) => e.stopPropagation()}
                        autoFocus
                      />
                    ) : (
                      <ChatItemTitle
                        onDoubleClick={(e) => {
                          e.stopPropagation();
                          startEditTitle(c.id, c.title || '');
                        }}
                      >
                        {c.title || 'Без названия'}
                      </ChatItemTitle>
                    )}
                    <DeleteBtn onClick={(e) => deleteChat(c.id, e)}>✕</DeleteBtn>
                  </ChatItem>
                ))}
              </ChatList>
            </FloatingSidebar>
            <MessagesArea>
              {isMobileLayout && (
                <MessagesHeader>
                  <MobileChatsBtn type="button" onClick={() => setIsSidebarOpen((prev) => !prev)}>
                    {isSidebarOpen ? 'Скрыть чаты' : 'Чаты'}
                  </MobileChatsBtn>
                </MessagesHeader>
              )}
              {showEmpty ? (
                <EmptyState>
                  Выберите чат или начните новый. Задайте вопрос о навигации или действиях в Visor.
                </EmptyState>
              ) : (
                <MessagesList>
                  {messages.map((m, i) => (
                    <MessageBubble key={i} role={m.role}>
                      {m.role === 'model' ? (
                        <MarkdownContent>
                          {renderSimpleMarkdown(m.text)}
                        </MarkdownContent>
                      ) : (
                        m.text
                      )}
                    </MessageBubble>
                  ))}
                  {streamingText && (
                    <MessageBubble role="model">
                      <MarkdownContent>
                        {renderSimpleMarkdown(streamingText)}
                      </MarkdownContent>
                    </MessageBubble>
                  )}
                  {loading && !streamingText && (
                    <MessageBubble role="model">…</MessageBubble>
                  )}
                  <div ref={messagesEndRef} />
                </MessagesList>
              )}
              <InputRow $recording={isRecording}>
                {isRecording ? (
                  <VoiceRecordingCard role="status" aria-live="polite">
                    <VoicePulseWrap>
                      <VoicePulseDot aria-hidden />
                      <VoiceRecordingMeta>
                        <VoiceRecordingTitleRow>
                          <VoiceRecordingTitle>Запись сообщения</VoiceRecordingTitle>
                          <VoiceTimer>{formatRecordingMmSs(recordElapsedSec)}</VoiceTimer>
                        </VoiceRecordingTitleRow>
                        <VoiceRecordingHint>
                          Говорите в микрофон. Нажмите «Готово», чтобы распознать текст, или «Отменить».
                          Максимум {Math.floor(SPEECH_TO_TEXT_MAX_MS / 60000)} мин.
                        </VoiceRecordingHint>
                      </VoiceRecordingMeta>
                    </VoicePulseWrap>
                    <VoiceWaveform aria-hidden>
                      {[0, 1, 2, 3, 4].map((i) => (
                        <VoiceWaveBar key={i} $delay={i * 95} />
                      ))}
                    </VoiceWaveform>
                    <VoiceRecordingActions>
                      <VoiceBtnGhost type="button" onClick={cancelVoiceRecording}>
                        Отменить
                      </VoiceBtnGhost>
                      <VoiceBtnPrimary type="button" onClick={finishVoiceRecording}>
                        Готово
                      </VoiceBtnPrimary>
                    </VoiceRecordingActions>
                  </VoiceRecordingCard>
                ) : (
                  <>
                    <InputWrap>
                      {speechLoading ? (
                        <VoiceProcessingStrip>
                          <SpinnerIcon aria-hidden />
                          <span>Распознаём речь… Обычно это занимает несколько секунд.</span>
                        </VoiceProcessingStrip>
                      ) : null}
                      <Input
                        ref={inputRef}
                        value={inputValue}
                        onChange={(e) => {
                          setInputValue(e.target.value);
                          adjustInputHeight();
                        }}
                        onKeyDown={handleKeyDown}
                        placeholder={
                          speechLoading
                            ? 'Дождитесь окончания распознавания…'
                            : 'Введите сообщение или запишите голосом…'
                        }
                        rows={1}
                        disabled={loading || speechLoading}
                      />
                      {voiceErr ? <VoiceHint role="alert">{voiceErr}</VoiceHint> : null}
                    </InputWrap>
                    <InputActions>
                      {voiceSupported ? (
                        <MicBtn
                          type="button"
                          $busy={speechLoading}
                          onClick={() => void startRecording()}
                          disabled={loading || speechLoading}
                          title="Записать голосовое сообщение"
                          aria-label="Записать голосовое сообщение"
                        >
                          {speechLoading ? <SpinnerIcon aria-hidden /> : <MicIcon />}
                        </MicBtn>
                      ) : null}
                      <SendBtn
                        type="button"
                        onClick={sendMessage}
                        disabled={loading || speechLoading || !inputValue.trim()}
                      >
                        Отправить
                      </SendBtn>
                    </InputActions>
                  </>
                )}
              </InputRow>
            </MessagesArea>
          </ChatLayout>
        </FullscreenInner>
      </Modal>
    );
  }
);
